import type { SupportedLanguage } from './constants';

// Temporary backward-compatibility layer for legacy locale keys.
// Remove after the migration window once legacy persisted values are no longer expected.
// Legacy/variant locale aliases mapped to canonical supported language keys.
const LEGACY_LOCALE_ALIASES: Record<string, SupportedLanguage> = {
  nl: 'nl_NL',
  'nl-NL': 'nl_NL'
};

/**
 * Type guard: narrows an arbitrary string to `SupportedLanguage` when it
 * appears in the supplied canonical list.
 *
 * Necessary because `.includes()` on a `readonly SupportedLanguage[]` rejects
 * arbitrary `string` arguments — the whole point of this check is to validate
 * an unknown value at runtime, so we cast through `readonly string[]` once
 * here and expose a typed predicate to callers.
 */
export const isSupportedLanguage = (
  value: string,
  supportedLanguages: readonly SupportedLanguage[]
): value is SupportedLanguage =>
  (supportedLanguages as readonly string[]).includes(value);

export const toSupportedLanguage = (
  locale: string | null | undefined,
  supportedLanguages: readonly SupportedLanguage[]
): SupportedLanguage | null => {
  if (!locale) {
    return null;
  }

  if (isSupportedLanguage(locale, supportedLanguages)) {
    return locale;
  }

  const aliased = LEGACY_LOCALE_ALIASES[locale];
  if (aliased && isSupportedLanguage(aliased, supportedLanguages)) {
    return aliased;
  }

  return null;
};

export const normalizeAndMigrateSelectedLanguage = (
  selectedLanguage: string | null,
  supportedLanguages: readonly SupportedLanguage[],
  storageKey: string
): SupportedLanguage | null => {
  const normalized = toSupportedLanguage(selectedLanguage, supportedLanguages);

  // One-time migration: replace legacy persisted locale codes with canonical values.
  if (selectedLanguage && normalized && selectedLanguage !== normalized) {
    localStorage.setItem(storageKey, normalized);
  }

  return normalized;
};
